/**
 * Layout Wrapper Component
 * 
 * A wrapper component that renders the appropriate layout based on the current layout type.
 * Handles layout switching and passes props to the active layout component.
 */

import { useMemo, useCallback } from 'react';
import { getLayout, layouts } from './index';
import type {
    LayoutId,
    BaseLayoutOptions,
    LayoutQuery,
    LayoutItem,
    LayoutSelection,
} from './types';
import type { Field } from '@/hooks/useFields';

interface LayoutWrapperProps {
    // Current layout type
    layout: LayoutId;

    // Collection info
    collection: string;
    primaryKeyField?: string;

    // Data
    items: LayoutItem[];
    loading?: boolean;
    error?: Error | null;

    // Pagination
    page: number;
    limit: number;
    totalCount?: number;
    itemCount?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    onLimitChange?: (limit: number) => void;

    // Selection
    selection: LayoutSelection;
    onSelectionChange: (selection: LayoutSelection) => void;
    selectMode?: boolean;
    showSelect?: 'none' | 'one' | 'multiple';

    // Sorting
    sort?: string[];
    onSortChange?: (sort: string[]) => void;

    // Layout configuration (persisted in presets)
    // Accept either a layout-specific object or a keyed record (backward-compatible)
    layoutOptions: Record<LayoutId, BaseLayoutOptions> | BaseLayoutOptions;
    onLayoutOptionsChange: (options: Record<LayoutId, BaseLayoutOptions> | BaseLayoutOptions) => void;
    layoutQuery: Record<LayoutId, LayoutQuery> | LayoutQuery;
    onLayoutQueryChange: (query: Record<LayoutId, LayoutQuery> | LayoutQuery) => void;

    // Fields info
    fields: Field[];

    // Actions
    onItemClick?: (item: LayoutItem) => void;
    onRefresh?: () => void;

    // Read-only mode
    readonly?: boolean;
}

export function LayoutWrapper({
    layout,
    collection,
    primaryKeyField = 'id',
    items,
    loading = false,
    error,
    page,
    limit,
    totalCount,
    itemCount,
    totalPages,
    onPageChange,
    onLimitChange,
    selection,
    onSelectionChange,
    selectMode = false,
    showSelect = 'multiple',
    sort = [],
    onSortChange,
    layoutOptions,
    onLayoutOptionsChange,
    layoutQuery,
    onLayoutQueryChange,
    fields,
    onItemClick,
    onRefresh,
    readonly = false,
}: LayoutWrapperProps) {

    // Get the layout configuration
    const layoutConfig = useMemo(() => {
        return getLayout(layout) || layouts.tabular;
    }, [layout]);

    // Get layout-specific options
    const currentLayoutOptions = useMemo(() => {
        const maybeRecord = layoutOptions as Record<LayoutId, BaseLayoutOptions>;
        if (maybeRecord && typeof maybeRecord === 'object' && layout in maybeRecord) {
            return (maybeRecord[layout] || {}) as BaseLayoutOptions;
        }
        return (layoutOptions as BaseLayoutOptions) || {};
    }, [layoutOptions, layout]);

    // Handle layout options change - store under layout key
    const handleLayoutOptionsChange = useCallback((options: BaseLayoutOptions) => {
        const maybeRecord = layoutOptions as Record<LayoutId, BaseLayoutOptions>;
        if (maybeRecord && typeof maybeRecord === 'object' && layout in maybeRecord) {
            onLayoutOptionsChange({
                ...maybeRecord,
                [layout]: options,
            });
        } else {
            onLayoutOptionsChange(options);
        }
    }, [layout, layoutOptions, onLayoutOptionsChange]);

    // Get layout-specific query
    const currentLayoutQuery = useMemo(() => {
        const maybeRecord = layoutQuery as Record<LayoutId, LayoutQuery>;
        if (maybeRecord && typeof maybeRecord === 'object' && layout in maybeRecord) {
            return (maybeRecord[layout] || {}) as LayoutQuery;
        }

        const maybeQuery = layoutQuery as LayoutQuery;
        if (maybeQuery && typeof maybeQuery === 'object' && ('fields' in maybeQuery || 'sort' in maybeQuery || 'limit' in maybeQuery || 'page' in maybeQuery)) {
            return maybeQuery;
        }

        return {
            fields: [],
            sort: [],
            limit: limit,
            page: page,
        } as LayoutQuery;
    }, [layoutQuery, layout, limit, page]);

    // Handle layout query change - store under layout key
    const handleLayoutQueryChange = useCallback((query: LayoutQuery) => {
        const maybeRecord = layoutQuery as Record<LayoutId, LayoutQuery>;
        if (maybeRecord && typeof maybeRecord === 'object' && layout in maybeRecord) {
            onLayoutQueryChange({
                ...maybeRecord,
                [layout]: query,
            });
        } else {
            onLayoutQueryChange(query);
        }

        if (query.page !== undefined && query.page !== page && onPageChange) {
            onPageChange(query.page);
        }
        if (query.limit !== undefined && query.limit !== limit && onLimitChange) {
            onLimitChange(query.limit);
        }
    }, [layout, layoutQuery, onLayoutQueryChange, page, limit, onPageChange, onLimitChange]);

    // Render the layout component
    const LayoutComponent = layoutConfig.component;

    return (
        <LayoutComponent
            collection={collection}
            primaryKeyField={primaryKeyField}
            items={items}
            loading={loading}
            error={error}
            page={page}
            limit={limit}
            totalCount={totalCount}
            itemCount={itemCount}
            totalPages={totalPages}
            selection={selection}
            onSelectionChange={onSelectionChange}
            selectMode={selectMode}
            showSelect={showSelect}
            sort={sort}
            onSortChange={onSortChange}
            layoutOptions={currentLayoutOptions}
            onLayoutOptionsChange={handleLayoutOptionsChange}
            layoutQuery={currentLayoutQuery}
            onLayoutQueryChange={handleLayoutQueryChange}
            fields={fields}
            onItemClick={onItemClick}
            onRefresh={onRefresh}
            readonly={readonly}
        />
    );
}

/**
 * Layout Options Wrapper
 * 
 * Renders the options component for the current layout.
 */
interface LayoutOptionsWrapperProps {
    layout: LayoutId;
    layoutOptions: Record<LayoutId, BaseLayoutOptions>;
    onLayoutOptionsChange: (options: Record<LayoutId, BaseLayoutOptions>) => void;
    collection: string;
    fields: Field[];
}

export function LayoutOptionsWrapper({
    layout,
    layoutOptions,
    onLayoutOptionsChange,
    collection,
    fields,
}: LayoutOptionsWrapperProps) {
    const layoutConfig = useMemo(() => {
        return getLayout(layout) || layouts.tabular;
    }, [layout]);

    const OptionsComponent = layoutConfig.optionsComponent;

    if (!OptionsComponent) {
        return null;
    }

    const currentLayoutOptions = useMemo(() => {
        return (layoutOptions?.[layout] || {}) as BaseLayoutOptions;
    }, [layoutOptions, layout]);

    const handleLayoutOptionsChange = useCallback((options: BaseLayoutOptions) => {
        onLayoutOptionsChange({
            ...layoutOptions,
            [layout]: options,
        } as Record<LayoutId, BaseLayoutOptions>);
    }, [layout, layoutOptions, onLayoutOptionsChange]);

    return (
        <OptionsComponent
            layoutOptions={currentLayoutOptions}
            onLayoutOptionsChange={handleLayoutOptionsChange}
            collection={collection}
            fields={fields}
        />
    );
}
